import React, { useState, useEffect } from 'react';
import { DoorOpen, DoorClosed, Wrench, Bell, CheckCircle, XCircle, ArrowRight, Link, Code, Terminal, Zap } from 'lucide-react';

type RoomState = 'available' | 'occupied' | 'maintenance';

const SynchronizedAnimations: React.FC = () => {
  const [currentState, setCurrentState] = useState<RoomState>('available');
  const [showNotification, setShowNotification] = useState(false);
  const [showResponse, setShowResponse] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [textCurrentState, setTextCurrentState] = useState<RoomState>('available');

  const states: RoomState[] = ['available', 'occupied', 'maintenance'];
  const colors: Record<RoomState, string> = {
    available: '#22c55e',
    occupied: '#ef4444',
    maintenance: '#f59e0b',
  };

  const stateIcons: Record<RoomState, React.ReactNode> = {
    available: <DoorOpen className="w-8 h-8" />,
    occupied: <DoorClosed className="w-8 h-8" />,
    maintenance: <Wrench className="w-8 h-8" />,
  };

  useEffect(() => {
    const interval = setInterval(() => {
      // Calculate next state
      const nextState = states[(states.indexOf(currentState) + 1) % states.length];
      
      // Start with API request
      setShowResponse(true);
      
      // After 1.5 seconds, show success
      setTimeout(() => {
        setShowSuccess(true);
        
        // After another 1.5 seconds, update room status first
        setTimeout(() => {
          // Update room status first
          setIsAnimating(true);
          setShowNotification(true);
          setCurrentState(nextState);
          
          // After 1 second, hide response
          setTimeout(() => {
            setShowResponse(false);
            setShowSuccess(false);
              // Reset animations after 2 seconds
              setTimeout(() => {
                setIsAnimating(false);
                setShowNotification(false);
                setTextCurrentState(nextState);
              }, 2000);
          }, 1000);
        }, 1500);
      }, 1500);
    }, 8000);

    return () => clearInterval(interval);
  }, [currentState]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* API Integration Animation */}
      <div className="relative p-8 bg-slate-900 rounded-2xl shadow-xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800" />
        
        <div className="flex items-center gap-2 mb-4">
          <div className="flex gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <div className="w-3 h-3 rounded-full bg-green-500" />
          </div>
          <div className="text-slate-400 text-sm ml-2">api.roomstate.site</div>
        </div>

        <div className="relative">
          <div className="mb-4">
            <div className="flex items-center gap-2 text-slate-400 mb-2">
              <Terminal className="w-4 h-4" />
              <span className="text-sm">Request</span>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4 font-mono text-sm">
              <div className="text-blue-400">curl -X POST</div>
              <div className="text-green-400">https://api.roomstate.site/v1/rooms/status</div>
              <div className="text-purple-400">-H "Authorization: Bearer YOUR_API_KEY"</div>
              <div className="text-yellow-400">-d {'{'} "state": "{states[(states.indexOf(textCurrentState) + 1) % states.length]}" {'}'}</div>
            </div>
          </div>

          <div className={`transition-all duration-500 ${showResponse ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="flex items-center gap-2 text-slate-400 mb-2">
              <Code className="w-4 h-4" />
              <span className="text-sm">Response</span>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4 font-mono text-sm">
              <div className="text-green-400">{'{'}</div>
              <div className="text-blue-400 ml-4">"status": "success",</div>
              <div className="text-blue-400 ml-4">"room_id": "rest-room",</div>
              <div className="text-blue-400 ml-4">"state": "{states[(states.indexOf(currentState) + 1) % states.length]}",</div>
              <div className="text-blue-400 ml-4">"updated_at": "2024-03-20T12:00:00Z"</div>
              <div className="text-green-400">{'}'}</div>
            </div>
          </div>

          <div className={`absolute -right-4 top-1/2 transform -translate-y-1/2 transition-all duration-500 ${
            showSuccess ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
          }`}>
            <div className="flex items-center gap-2 bg-green-500/20 text-green-400 px-3 py-1 rounded-full">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm">Success</span>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4">
          <div className="flex items-center gap-2 text-slate-400">
            <Zap className="w-4 h-4 text-yellow-500" />
            <span className="text-sm">Real-time Updates</span>
          </div>
          <div className="flex items-center gap-2 text-slate-400">
            <Zap className="w-4 h-4 text-yellow-500" />
            <span className="text-sm">Webhook Support</span>
          </div>
          <div className="flex items-center gap-2 text-slate-400">
            <Zap className="w-4 h-4 text-yellow-500" />
            <span className="text-sm">Rate Limiting</span>
          </div>
          <div className="flex items-center gap-2 text-slate-400">
            <Zap className="w-4 h-4 text-yellow-500" />
            <span className="text-sm">API Keys</span>
          </div>
        </div>
      </div>

      {/* Room Status Animation */}
      <div className="relative p-12 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-3xl shadow-xl border border-slate-200/50 dark:border-slate-700/50 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-purple-50/50 dark:from-blue-900/20 dark:to-purple-900/20" />
        
        <div className="relative flex flex-col items-center">
          <div className="relative w-64 h-64 mb-8">
            <div className="absolute inset-0 border-8 border-slate-300 dark:border-slate-600 rounded-2xl" />
            
            <div 
              className={`absolute inset-4 rounded-xl transition-all duration-500 flex items-center justify-center ${
                isAnimating ? 'scale-95 opacity-70' : 'scale-100 opacity-100'
              }`}
              style={{ backgroundColor: colors[currentState] }}
            >
              <div className="text-white transform transition-transform duration-500">
                {stateIcons[currentState]}
              </div>
            </div>

            <div 
              className={`absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-full text-white font-medium transition-all duration-500 ${
                isAnimating ? 'scale-105' : 'scale-100'
              }`}
              style={{ 
                backgroundColor: colors[currentState],
                boxShadow: `0 0 20px ${colors[currentState]}40`
              }}
            >
              {currentState.charAt(0).toUpperCase() + currentState.slice(1)}
            </div>
          </div>

          <div className="relative w-full max-w-md">
            <div className={`relative bg-slate-100 dark:bg-slate-700 rounded-xl p-4 transition-all duration-500 ${
              showNotification ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}>
              <div className="flex items-center gap-3">
                <Link className="w-5 h-5 text-slate-500" />
                <div className="flex-1 text-sm font-mono text-slate-600 dark:text-slate-300">
                  roomstate.site/b/rest-room
                </div>
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              </div>
            </div>

            <div className={`absolute -right-4 top-1/2 transform -translate-y-1/2 transition-all duration-500 ${
              showNotification ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
            }`}>
              <div className="relative">
                <Bell className="w-6 h-6 text-blue-500 animate-bounce" />
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SynchronizedAnimations; 